import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Area, AreaChart, Bar, BarChart, Legend, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { styled } from 'styled-components';
import { strings } from '/imports/strings';
import { fileHref } from '/imports/api/files';
import { css } from '/imports/ui/css';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { useFindOne, useMethod, useUser } from '/imports/hooks';
import { Accounts, Roles } from '/imports/api/accounts';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { capitalize, defer, formatCurrency, formatNumber, mapById, objectKeys, objectValues, randomColor } from '/imports/utils';
import { Dict } from '/imports/types';
import { Org } from '/imports/api/orgs';
import { ProductionMeter } from '/imports/api/productionMeters';
import { Input } from '/imports/ui/Input';
import { Checkbox } from '/imports/ui/Checkbox';
import { DateInput } from '/imports/ui/DateInput';
import { DateRangePresetSelect } from '/imports/ui/DateRangePresetSelect';
import { Button } from '/imports/ui/Button';
import { toastOnError } from '/imports/ui/utils';
import { DataCatalog } from '/imports/ui/DataCatalog';
import {
  ricDashboardProductionMeters, ricDashboardQuarterlyChartData, ricDashboardQuarterlyTotals, ricDashboardSystemChartData, ricDashboardSystems,
  ricDashboardSystemTableData, ricDashboardYtdTotals, ricSolar25YearRevenueChartData, ricSolarYtdRevenueChartData, Ric25YearRevenueData, SystemChartData, SystemTableData
} from '/imports/api/ricDashboard';

/**
 * Recharts sizes the Y axis in pixels, and 120px of a phone-width card is a third of the plot area.
 * @param desktop - the width to keep above the mobile breakpoint
 */
const yAxisWidth = (desktop: number) => (window.matchMedia(`(max-width: ${css.mobileWidth})`).matches ? 70 : desktop);

const RicDashboardPageStyled = styled(Page)`
  .solar-revenue-chart {
    margin-top: 2rem;
  }
  .system-production-chart {
    margin-top: 2rem;
  }
  .system-table {
    margin-top: 2rem;
  }
`;

/** Realty Income year-to-date dashboard: aggregates, revenue chart, production chart, and system table. */
export const RicDashboardPageYtd = () => {
  const user = useUser();
  const userId = user?._id;
  const orgId = user?.orgId;
  const account = useFindOne(() => Accounts.find({ orgId, userId }), [`${orgId}_${userId}`]);
  const [systems, loadingSystems] = useMethod(ricDashboardSystems, undefined);
  const [ytdTotals, loadingYtdTotals] = useMethod(ricDashboardYtdTotals, undefined);
  const [solarRevenueData, loadingSolarRevenueData] = useMethod(ricSolarYtdRevenueChartData, undefined);
  const [productionMeters, loadingProductionMeters] = useMethod(ricDashboardProductionMeters, undefined);
  const meterIds = productionMeters?.map((m) => m._id) || [];
  const startDate = DateTime.now().plus({ days: -14 }).toFormat('yyyy-MM-dd');
  const endDate = DateTime.now().toFormat('yyyy-MM-dd');
  const [systemProductionData, loadingSystemProductionData] = useMethod(ricDashboardSystemChartData, { meterIds, startDate, endDate }, [productionMeters], (deps) => deps[0]?.length);
  const [systemTableData, loadingSystemTableDate] = useMethod(ricDashboardSystemTableData, undefined);
  const loading = loadingSystems || loadingYtdTotals || loadingSolarRevenueData || loadingProductionMeters || loadingSystemProductionData || loadingSystemTableDate;
  // Decided before the spinner, and tolerant of an account that has not arrived yet: the methods refuse
  // a caller without the role, so waiting on them left someone who may not be here watching a spinner
  // that never resolves.
  if (user && !user.isSuperUser && account && !account.roles?.includes(Roles.ricExecutive)) return <NotPermittedPage />;
  if (loading || !solarRevenueData) return <LoadingFlex />;
  return (
    <RicDashboardPageStyled>
      <PageHeading
        left={<h1>Realty Income Dashboard</h1>}
        right={
          <div>
            <Button>Year to Date</Button>
            <Link to="/ric-dashboard/quarterly"><Button className="btn secondary">Quarterly</Button></Link>
            <Link to="/ric-dashboard/25-year"><Button className="btn secondary">25-Year</Button></Link>
          </div>
        }
      />
      <PageBody>
        <RicDashboardAggregates
          items={[
            { label: 'Total Number of Systems', value: `${systems?.numSystems || 0}` },
            { label: 'Total System Size', value: `${formatNumber(systems?.systemSize || 0)} kW DC` },
            { label: 'YTD Production', value: `${formatNumber(ytdTotals?.production || 0)} kWh` },
            { label: 'YTD Revenue', value: `${formatCurrency(ytdTotals?.revenue || 0, 0)}` },
            { label: 'Forecasted Revenue', value: `${formatCurrency(ytdTotals?.forecastedRevenue || 0, 0)}` },
          ]}
        />
        <RicDashboardSolarRevenueChart data={solarRevenueData} subYear="month" metric="revenue" />
        <RicDashboardSystemProductionChart productionMeters={productionMeters || []} data={systemProductionData} metric="production" startDate={startDate} endDate={endDate} />
        <RicDashboardSystemTable productionMeters={systemTableData} />
      </PageBody>
    </RicDashboardPageStyled>
  );
};

const RicDashboardAggregatesStyled = styled.div`
  display: flex;
  gap: 1rem;
  .box {
    border: ${css.inputBorder};
    border-radius: ${css.inputBorderRadius};
    padding: 1rem;
    box-sizing: border-box;
    text-align: center;
    font-size: 1.25rem;
    font-weight: bold;
    color: ${css.green};
    flex: 1;
  }
  ${css.mobileMedia} {
    /* The row does not wrap, so at 390px three boxes were crushed to ~120px each — the labels broke
       onto five lines and the third box ran off the edge. Wrap them two-up instead. */
    flex-wrap: wrap;
    .box {
      flex: 1 1 40%;
      font-size: 1rem;
      padding: 0.75rem 0.5rem;
    }
  }
`;

interface RicDashboardAggregatesProps {
  items: { label: string, value: string }[];
}

/** Renders a row of labeled aggregate value boxes. */
export const RicDashboardAggregates = (props: RicDashboardAggregatesProps) => {
  const { items } = props;
  return (
    <RicDashboardAggregatesStyled>
      {
        items.map((item) => (
          <div key={item.label} className="box">
            <div>{item.label}:</div>
            <div>{item.value}</div>
          </div>
        ))
      }
    </RicDashboardAggregatesStyled>
  );
};

const RicDashboardSolarRevenueChartStyled = styled.div`
  border: ${css.inputBorder};
  border-radius: ${css.inputBorderRadius};
  padding: 2rem;
  box-sizing: border-box;
  h1 {
    margin: 0;
  }
  .chart-wrapper {
    width: 100%;
    height: 20rem;
    box-sizing: border-box;
  }
  ${css.mobileMedia} {
    padding: 1rem;
    h1 { font-size: 1.5rem; }
    .chart-wrapper { height: 16rem; }
  }
`;

interface RicDashboardSolarRevenueChartProps {
  data: {
    records: {
      key: string;
      [orgKey: string]: number | string;
    }[];
    orgMap: Dict<Org>;
  },
  subYear: 'month' | 'quarter',
  metric: 'production' | 'revenue',
}

/** Stacked bar chart of solar revenue (or production) by org over months/quarters, highlighting the current period. */
export const RicDashboardSolarRevenueChart = (props: RicDashboardSolarRevenueChartProps) => {
  const { data, subYear, metric } = props;
  const { orgMap, records } = data;
  const now = DateTime.now().setZone('America/Los_Angeles');
  const currentYear = now.year;
  const currentMonth = now.month;
  const currentQuarter = now.quarter;
  const currentSubyear = subYear === 'quarter' ? currentQuarter : currentMonth;
  const currentKey = records.find(r => {
    const [year, sub] = r.key.split('-');
    return parseInt(year) === currentYear && parseInt(sub) === currentSubyear;
  })?.key;
  return (
    <RicDashboardSolarRevenueChartStyled className="solar-revenue-chart">
      <h1>{strings.SolarRevenue()}</h1>
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={records}
          >
            <XAxis dataKey="key" name="key" domain={['dataMin', 'dataMax']} tickFormatter={(value) => {
              const [year, subYear] = value.split('-');
              if (parseInt(year) === currentYear && parseInt(subYear) === currentSubyear) return `${value} (${strings.Current()})`;
              return value;
            }} />
            <defs>
              <pattern id="currentMonthPattern" width="16" height="16" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width="16" height="16" fill="#eef8ff" />
                <line x1="0" y1="0" x2="0" y2="16" stroke="#e0efff" strokeWidth="16" />
              </pattern>
            </defs>
            {currentKey && <ReferenceArea x1={currentKey} x2={currentKey} fill="url(#currentMonthPattern)" fillOpacity={0.8} label={{ value: strings.Current(), position: 'insideTop', fill: '#888', fontSize: 12 }} />}
            {
              metric === 'production' ?
                <YAxis tickFormatter={(value) => `${formatNumber(value)} kWh`} width={yAxisWidth(100)} />
                :
                <YAxis tickFormatter={(value) => `${formatCurrency(value, 0)}`} width={yAxisWidth(100)} />
            }
            <ReferenceLine y={0} stroke="#000" />
            {
              metric === 'production' ?
                <Tooltip formatter={(value: number) => `${formatNumber(value, 2)} kWh`} />
                :
                <Tooltip formatter={(value: number) => `$${formatNumber(value, 2)}`} />
            }
            <Legend />
            {objectValues(orgMap).map((o) =>
              <Bar key={o._id} dataKey={metric === 'production' ? `${o._id}_production` : `${o._id}_revenue`} barSize={40} stackId="a" name={`${orgMap[o._id].name}`} fill={orgMap[o._id].primaryColor} isAnimationActive={false} />
            )}
            <Bar dataKey={`forecasted_${metric}`} barSize={20} stackId="a" name={strings.Forecasted()} fill={css.grey6} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </RicDashboardSolarRevenueChartStyled>
  );
};

const RicDashboardSystemProductionChartStyled = styled.div`
  border: ${css.inputBorder};
  border-radius: ${css.inputBorderRadius};
  padding: 2rem;
  box-sizing: border-box;
  position: relative;
  h1 {
    margin: 0;
  }
  .wrapper {
    display: flex;
    width: 100%;
    height: 20rem;
    box-sizing: border-box;
  }
  .system-picker {
    border: ${css.inputBorder};
    border-radius: ${css.inputBorderRadius};
    height: 20rem;
    width: 28rem;
    display: flex;
    flex-direction: column;
    .system-search {
      margin: 0.5rem;
      box-sizing: border-box;
    }
    .system-list {
      overflow: auto;
      flex: 1;
    }
    .list-item {
      border-bottom: ${css.inputBorder};
      display: flex;
      align-items: center;
      [type=checkbox] {
        position: relative;
        top: 3px;
      }
    }
  }
  .chart-wrapper {
    height: 20rem;
    width: 100%;
    position: relative;
    .recharts-tooltip-wrapper { transform: none !important; cursor: none; }
    .recharts-default-tooltip { overflow-y: auto; .recharts-tooltip-item-list { max-height: 10rem; } }
  }
  .timerange-picker {
    display: flex;
    gap: 0.25rem;
    align-items: center;
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    select {
      width: auto;
      margin-right: 0.5rem;
    }
  }
  .metric-picker {
    display: flex;
    gap: 1rem;
    align-items: center;
    position: absolute;
    top: 0.5rem;
    left: 2rem;
    div {
      font-size: 1.1rem;
      cursor: pointer;
      &.selected {
        font-weight: bold;
        color: ${css.primaryColor};
      }
    }
  }
  .chart-total {
    text-align: center;
    font-weight: bold;
    font-size: 1.1rem;
    padding-left: 100px;
    box-sizing: border-box;
  }
  ${css.mobileMedia} {
    /* The chart total overflows the fixed-height chart box by design; last in the column it needs room. */
    padding: 1rem 1rem 3rem;
    /* Stacked: side by side, the 28rem picker left the chart ~0px wide. */
    .wrapper { flex-direction: column; height: auto; gap: 0.75rem; }
    /* Both control clusters are absolutely positioned against the card on desktop, which put the
       time-range one 643px wide and off the right edge. In flow they land after the chart in DOM
       order, so re-order the column: controls, then system picker, then chart. */
    .metric-picker { position: static; order: 1; }
    .timerange-picker {
      position: static;
      order: 2;
      flex-wrap: wrap;
      /* Left to wrap freely the range stranded the word "to" and pushed Update onto its own line. */
      gap: 0.4rem 0.5rem;
      select { margin-right: 0; }
      .input, input { min-width: 0; }
      .btn { margin-left: auto; }
    }
    .system-picker { order: 3; width: 100%; height: 14rem; }
    .chart-wrapper { order: 4; height: 16rem; }
    .chart-total { padding-left: 0; }
  }
`;

interface RicDashboardSystemProductionChartProps {
  startDate?: string;
  endDate?: string;
  productionMeters: ProductionMeter[];
  data?: SystemChartData;
  metric: 'production' | 'revenue';
}

/** Interactive system production/revenue chart with meter picker, date range, and metric toggle. */
export const RicDashboardSystemProductionChart = (props: RicDashboardSystemProductionChartProps) => {
  const { productionMeters } = props;
  const [data, setData] = useState(props.data);
  const [startDate, setStartDate] = useState(props.startDate);
  const [endDate, setEndDate] = useState(props.endDate);
  const [selectAll, setSelectAll] = useState(true);
  const [selectedMeterIds, setSelectedMetersIds] = useState(productionMeters.map((m) => m._id));
  const [metric, setMetric] = useState(props.metric);
  const [search, setSearch] = useState<string>();
  const [preset, setPreset] = useState('custom');
  const onPresetChange = (value: string, newStartDate?: string, newEndDate?: string) => {
    setPreset(value);
    if (newStartDate) setStartDate(newStartDate);
    if (newEndDate) setEndDate(newEndDate);
    if (newStartDate && newEndDate) updateData({ startDate: newStartDate, endDate: newEndDate });
  };
  const filteredMeters = useMemo(() => productionMeters.filter((m) => {
    const _search = search?.trim().toLowerCase();
    if (!_search) return true;
    return m.name.toLowerCase().includes(_search);
  }), [productionMeters, search]);
  const colorMap = useMemo(() => {
    return productionMeters.reduce((map: Dict<string>, m) => {
      map[m._id] = m.chartColor || randomColor();
      return map;
    }, {});
  }, [productionMeters]);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const mouseHandler = (e: MouseEvent) => {
      const wrappers = Array.from(ref.current?.querySelectorAll('.recharts-tooltip-wrapper') || []) as HTMLDivElement[];
      for (const wrapper of wrappers) {
        const rect = wrapper.getBoundingClientRect();
        const width = rect.width;
        const leftOffset = Math.max(e.clientX + width - window.innerWidth, 0);
        wrapper.style.left = `${e.clientX - 10 - leftOffset}px`;
        wrapper.style.top = `${e.clientY - 10}px`;
      }
    };
    window.addEventListener('mousemove', mouseHandler);
    return () => window.removeEventListener('mousemove', mouseHandler);
  }, []);
  useEffect(() => {
    if (selectAll) setSelectedMetersIds(productionMeters.map((m) => m._id));
    else setSelectedMetersIds([]);
  }, [selectAll]);
  const allMeterIds = useMemo(() => productionMeters.map((m) => m._id), [productionMeters]);
  const updateData = toastOnError(async (overrides?: { startDate?: string, endDate?: string }) => defer(async () => {
    const _startDate = overrides?.startDate || startDate;
    const _endDate = overrides?.endDate || endDate;
    if (!_startDate || !_endDate) return;
    setData(await ricDashboardSystemChartData.call({ meterIds: allMeterIds, startDate: _startDate, endDate: _endDate }));
  }));
  const productionMeterMap = useMemo(() => mapById(productionMeters), [productionMeters]);
  const selectedMeterKey = selectedMeterIds.join(',');
  const selectedMeters = useMemo(() => productionMeters.filter((m) => selectedMeterIds.includes(m._id)), [productionMeters, selectedMeterKey]);
  const filteredTotal = useMemo(() => {
    let production = 0;
    let revenue = 0;
    for (const record of data?.records || []) {
      for (const meterId of selectedMeterIds) {
        production += Number(record[`${meterId}_production`]) || 0;
        revenue += Number(record[`${meterId}_revenue`]) || 0;
      }
    }
    return { production, revenue };
  }, [data?.records, selectedMeterKey]);
  if (!data) return null;
  let records = data.records;
  const useAreaChart = records[0]?.key.includes(':');
  if (useAreaChart && records.length) {
    const prevKey = DateTime.fromFormat(records[0].key, 'yyyy-MM-dd HH:mm').plus({ hours: -1 }).toFormat('yyyy-MM-dd HH:mm');
    const nextKey = DateTime.fromFormat(records[records.length - 1].key, 'yyyy-MM-dd HH:mm').plus({ hours: 1 }).toFormat('yyyy-MM-dd HH:mm');
    const dataKeys = objectKeys(records[0]).filter((k) => k !== 'key');
    const dummyData = dataKeys.reduce((d: any, k) => ({ ...d, [k]: 0 }), {});
    records = [{ key: prevKey, ...dummyData }, ...records, { key: nextKey, ...dummyData }];
  }
  return (
    <RicDashboardSystemProductionChartStyled ref={ref} className="system-production-chart">
      <h1>{strings.Systems()}</h1>
      <div className="wrapper">
        <div className="system-picker">
          <div className="system-search">
            <Input value={search} onChange={(search) => setSearch(search)} placeholder={strings.Search()} />
          </div>
          <div className="list-item"><Checkbox checked={selectAll} onChange={(checked) => setSelectAll(checked)} />{strings.SelectAll()}</div>
          <div className="system-list">
            {
              filteredMeters.map((m) => (
                <div key={m._id} className="list-item">
                  <Checkbox
                    checked={selectedMeterIds?.includes(m._id)}
                    onChange={(checked) => {
                      if (checked) setSelectedMetersIds([...selectedMeterIds, m._id]);
                      else setSelectedMetersIds(selectedMeterIds.filter((_id) => _id != m._id));
                    }} />
                  {m.name}
                </div>
              ))
            }
          </div>
        </div>
        <div className="chart-wrapper">
          <ResponsiveContainer width="100%" height="100%">
            {useAreaChart ? (
              <AreaChart data={records}>
                <XAxis dataKey="key" name="key" domain={['dataMin', 'dataMax']} />
                {
                  metric === 'production' ?
                    <YAxis tickFormatter={(value) => `${formatNumber(value)} kWh`} width={yAxisWidth(120)} />
                    :
                    <YAxis tickFormatter={(value) => `${formatCurrency(value, 0)}`} width={yAxisWidth(120)} />
                }
                <ReferenceLine y={0} stroke="#000" />
                {
                  metric === 'production' ?
                    <Tooltip formatter={(value: number) => `${formatNumber(value, 2)} kWh`} wrapperStyle={{ background: css.white, zIndex: 100000, pointerEvents: 'auto', position: 'fixed' }} />
                    :
                    <Tooltip formatter={(value: number) => `$${formatNumber(value, 2)}`} wrapperStyle={{ background: css.white, zIndex: 100000, pointerEvents: 'auto', position: 'fixed' }} />
                }
                {selectedMeters.map((o) =>
                  <Area key={o._id} type="step" dataKey={metric === 'production' ? `${o._id}_production` : `${o._id}_revenue`} stackId="a" name={productionMeterMap[o._id]?.name} fill={colorMap[o._id]} stroke={colorMap[o._id]} fillOpacity={0.85} isAnimationActive={false} />
                )}
                <Area type="step" dataKey={`forecasted_${metric}`} stackId="a" name={strings.Forecasted()} fill={css.grey6} stroke={css.grey6} fillOpacity={0.5} isAnimationActive={false} />
              </AreaChart>
            ) : (
              <BarChart data={records}>
                <XAxis dataKey="key" name="key" domain={['dataMin', 'dataMax']} />
                {
                  metric === 'production' ?
                    <YAxis tickFormatter={(value) => `${formatNumber(value)} kWh`} width={yAxisWidth(120)} />
                    :
                    <YAxis tickFormatter={(value) => `${formatCurrency(value, 0)}`} width={yAxisWidth(120)} />
                }
                <ReferenceLine y={0} stroke="#000" />
                {
                  metric === 'production' ?
                    <Tooltip formatter={(value: number) => `${formatNumber(value, 2)} kWh`} wrapperStyle={{ background: css.white, zIndex: 100000, pointerEvents: 'auto', position: 'fixed' }} />
                    :
                    <Tooltip formatter={(value: number) => `$${formatNumber(value, 2)}`} wrapperStyle={{ background: css.white, zIndex: 100000, pointerEvents: 'auto', position: 'fixed' }} />
                }
                {selectedMeters.map((o) =>
                  <Bar key={o._id} dataKey={metric === 'production' ? `${o._id}_production` : `${o._id}_revenue`} barSize={40} stackId="a" name={productionMeterMap[o._id]?.name} fill={colorMap[o._id]} isAnimationActive={false} />
                )}
                <Bar dataKey={`forecasted_${metric}`} barSize={20} stackId="a" name={strings.Forecasted()} fill={css.grey6} isAnimationActive={false} />
              </BarChart>
            )}
          </ResponsiveContainer>
          <div className="chart-total">Total {capitalize(metric)}: {metric === 'production' ? `${formatNumber(filteredTotal.production)} kWh` : `${formatCurrency(filteredTotal.revenue, 0)}`}</div>
        </div>
        <div className="timerange-picker">
          <DateRangePresetSelect value={preset} onChange={onPresetChange} />
          <DateInput value={startDate} onChange={(startDate) => { setPreset('custom'); setStartDate(startDate); }} />
          to
          <DateInput value={endDate} onChange={(endDate) => { setPreset('custom'); setEndDate(endDate); }} />
          <Button onClick={() => updateData()}>{strings.Update()}</Button>
        </div>
        <div className="metric-picker">
          <div className={metric === 'production' ? 'selected' : ''} onClick={() => setMetric('production')}>Production</div>
          <div className={metric === 'revenue' ? 'selected' : ''} onClick={() => setMetric('revenue')}>Revenue</div>
        </div>
      </div>
    </RicDashboardSystemProductionChartStyled>
  );
};

const RicDashboardSystemTableStyled = styled.div`
  border: ${css.inputBorder};
  border-radius: ${css.inputBorderRadius};
  padding: 2rem;
  box-sizing: border-box;
  position: relative;
  height: 40rem;
  table {
    table-layout: fixed;
    thead {
      th {
        &.row-select, &.row-actions {
          display: none;
        }
      }
    }
    tbody {
      td {
        &.row-select, &.row-actions {
          display: none;
        }
      }
    }
  }
  /* td {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  } */
  ${css.mobileMedia} {
    width: 100%;
    padding: 1rem;
    /* The table is wider than any phone; it scrolls inside the card rather than stretching the page. */
    height: auto;
    max-height: 32rem;
    /* table-layout is fixed here with rem column widths, so the shared nowrap rule clipped the headers
       mid-word ("Today's Reve"). A fixed cell cannot grow to fit — let these wrap instead. */
    table thead th { white-space: normal; }
  }
`;

interface RicDashboardSystemTableProps {
  productionMeters?: SystemTableData;
}

/** Full-width data table of per-system production, revenue, and billing details. */
export const RicDashboardSystemTable = (props: RicDashboardSystemTableProps) => {
  const { productionMeters } = props;
  const ref = useRef<HTMLDivElement>(null);
  const calcWidth = () => {
    // Mobile keeps the sidenav mounted but slides it off-canvas, so its width is not taken from the
    // viewport — subtracting it here left the card 38px wide. Undefined hands sizing back to the CSS.
    if (window.matchMedia(`(max-width: ${css.mobileWidth})`).matches) return undefined;
    const navWidth = document.getElementById('sidenav')?.getBoundingClientRect().width || 0;
    return `${window.innerWidth - navWidth - 64}px`;
  };
  const [containerWidth, setContainerWidth] = useState(calcWidth());
  useEffect(() => {
    const recalc = () => setContainerWidth(calcWidth());
    window.addEventListener('resize', recalc);
    const sidenav = document.getElementById('sidenav');
    const observer = sidenav ? new ResizeObserver(recalc) : undefined;
    if (sidenav && observer) observer.observe(sidenav);
    return () => {
      window.removeEventListener('resize', recalc);
      observer?.disconnect();
    };
  }, []);
  if (!productionMeters) return null;
  return (
    <RicDashboardSystemTableStyled ref={ref} className="system-table" style={{ width: containerWidth }}>
      <DataCatalog
        allIds={productionMeters.map((m) => m._id)}
        data={productionMeters}
        columns={[
          { field: 'name', label: strings.System(), width: '20rem' },
          { field: 'size', label: `${strings.System()} (kW DC)`, width: '6rem' },
          { field: 'todaysProduction', label: `${strings.TodaysProduction()} (kWh)`, width: '6rem', render: (d) => formatNumber(d.todaysProduction, 1) },
          { field: 'todaysRevenue', label: `${strings.TodaysRevenue()}`, width: '6rem', render: (d) => formatCurrency(d.todaysRevenue) },
          { field: 'ytdProduction', label: `${strings.YtdProduction()} (kWh)`, width: '7rem', render: (d) => formatNumber(d.ytdProduction, 1) },
          { field: 'ytdRevenue', label: `${strings.YtdRevenue()}`, width: '8rem', render: (d) => formatCurrency(d.ytdRevenue) },
          { field: 'forecastedRevenue', label: `${strings.ForecastedRevenue()}`, width: '8rem', render: (d) => formatCurrency(d.forecastedRevenue, 0) },
          { field: 'commissioningDate', label: strings.CommissioningDate(), width: '8rem', render: (d) => d.commissioningDate ? DateTime.fromJSDate(d.commissioningDate, { zone: d.timezone }).toFormat('MM/dd/yyyy') : '-' },
          { field: 'latestBill', label: strings.LatestBill(), width: '8rem', render: (d) => d.lastBillIssuedDate ? <a href={fileHref(d.lastBillPdf)} target="_blank" rel="noreferrer">{DateTime.fromFormat(d.lastBillIssuedDate, 'yyyy-MM-dd').toFormat('MM/dd/yyyy')}</a> : '-' },
        ]}
      />
    </RicDashboardSystemTableStyled>
  );
};

/** Realty Income quarterly dashboard: aggregates and a quarterly production/revenue chart. */
export const RicDashboardPageQuarterly = () => {
  const user = useUser();
  const userId = user?._id;
  const orgId = user?.orgId;
  const account = useFindOne(() => Accounts.find({ orgId, userId }), [`${orgId}_${userId}`]);
  const [systems, loadingSystems] = useMethod(ricDashboardSystems, undefined);
  const [quarterlyTotals, loadingQuarterlyTotals] = useMethod(ricDashboardQuarterlyTotals, undefined);
  const [solarProductionRevenueData, loadingSolarProductionRevenueData] = useMethod(ricDashboardQuarterlyChartData, undefined);
  const loading = loadingSystems || loadingQuarterlyTotals || loadingSolarProductionRevenueData;
  if (user && !user.isSuperUser && account && !account.roles?.includes(Roles.ricExecutive)) return <NotPermittedPage />;
  if (loading || !solarProductionRevenueData) return <LoadingFlex />;
  return (
    <RicDashboardPageStyled>
      <PageHeading
        left={<h1>Realty Income Dashboard</h1>}
        right={
          <div>
            <Link to="/ric-dashboard/ytd"><Button className="btn secondary">Year to Date</Button></Link>
            <Button>Quarterly</Button>
            <Link to="/ric-dashboard/25-year"><Button className="btn secondary">25-Year</Button></Link>
          </div>
        }
      />
      <PageBody>
        <RicDashboardAggregates
          items={[
            { label: 'Total Number of Systems', value: `${systems?.numSystems || 0}` },
            { label: 'Total System Size', value: `${formatNumber(systems?.systemSize || 0)} kW DC` },
            { label: 'Last 4-Quarters Production', value: `${formatNumber(quarterlyTotals?.lastFourQuartersProduction || 0)} kWh` },
            { label: 'Next 4-Quarters Production', value: `${formatNumber(quarterlyTotals?.nextFourQuartersProduction || 0)} kWh` },
            { label: 'Last 4-Quarters Revenue', value: `${formatCurrency(quarterlyTotals?.lastFourQuartersRevenue || 0, 0)}` },
            { label: 'Next 4-Quarters Revenue', value: `${formatCurrency(quarterlyTotals?.nextFourQuartersRevenue || 0, 0)}` },
          ]}
        />
        <RicDashboardQuarterlyProductionRevenueChart data={solarProductionRevenueData} />
      </PageBody>
    </RicDashboardPageStyled>
  );
};

const RicDashboardQuarterlyProductionRevenueChartStyled = styled.div`
  border: ${css.inputBorder};
  border-radius: ${css.inputBorderRadius};
  padding: 2rem;
  box-sizing: border-box;
  h1 {
    margin: 0;
  }
  .chart-wrapper {
    width: 100%;
    height: 20rem;
    box-sizing: border-box;
    &.production {
      margin-top: 2rem;
    }
  }
  ${css.mobileMedia} {
    padding: 1rem;
    h1 { font-size: 1.5rem; }
    .chart-wrapper { height: 16rem; }
  }
`;

interface RicDashboardQuarterlyProductionRevenueChartProps {
  data: {
    records: {
      key: string;
      [orgKey: string]: number | string;
    }[];
    orgMap: Dict<Org>;
  },
}

/** Paired quarterly bar charts of revenue and production by org, highlighting the current quarter. */
export const RicDashboardQuarterlyProductionRevenueChart = (props: RicDashboardQuarterlyProductionRevenueChartProps) => {
  const { data } = props;
  const { orgMap, records } = data;
  const now = DateTime.now().setZone('America/Los_Angeles');
  const currentYear = now.year;
  const currentQuarter = now.quarter;
  const currentKey = records.find(r => {
    const [year, sub] = r.key.split('-');
    return parseInt(year) === currentYear && sub === `Q${currentQuarter}`;
  })?.key;

  return (
    <RicDashboardQuarterlyProductionRevenueChartStyled className="solar-revenue-chart">
      <h1>{strings.SolarRevenue()}</h1>
      <div className="chart-wrapper revenue">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={records}
          >
            <XAxis dataKey="key" name="key" domain={['dataMin', 'dataMax']} tickFormatter={(value) => {
              const [year, subYear] = value.split('-');
              if (parseInt(year) === currentYear && parseInt(subYear) === currentQuarter) return `${value} (${strings.Current()})`;
              return value;
            }} />
            <defs>
              <pattern id="currentQuarterPattern" width="16" height="16" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width="16" height="16" fill="#eef8ff" />
                <line x1="0" y1="0" x2="0" y2="16" stroke="#e0efff" strokeWidth="16" />
              </pattern>
            </defs>
            {currentKey && <ReferenceArea x1={currentKey} x2={currentKey} fill="url(#currentQuarterPattern)" fillOpacity={0.8} label={{ value: strings.Current(), position: 'insideTop', fill: '#888', fontSize: 12 }} />}
            <YAxis tickFormatter={(value) => formatCurrency(value, 0)} width={yAxisWidth(120)} />
            <ReferenceLine y={0} stroke="#000" />
            <Tooltip formatter={(value: number) => `$${formatNumber(value, 2)}`} />
            <Legend />
            {objectValues(orgMap).map((o) =>
              <Bar key={o._id} dataKey={`${o._id}_revenue`} barSize={40} stackId="a" name={`${orgMap[o._id].name}`} fill={orgMap[o._id].primaryColor} isAnimationActive={false} />
            )}
            <Bar dataKey="forecasted_revenue" barSize={20} stackId="a" name={strings.Forecasted()} fill={css.grey6} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <h1>{strings.SolarProduction()}</h1>
      <div className="chart-wrapper production">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={records}
          >
            <XAxis dataKey="key" name="key" domain={['dataMin', 'dataMax']} tickFormatter={(value) => {
              const [year, subYear] = value.split('-');
              if (parseInt(year) === currentYear && parseInt(subYear) === currentQuarter) return `${value} (${strings.Current()})`;
              return value;
            }} />
            {currentKey && <ReferenceArea x1={currentKey} x2={currentKey} fill="url(#currentQuarterPattern)" fillOpacity={0.8} label={{ value: strings.Current(), position: 'insideTop', fill: '#888', fontSize: 12 }} />}
            <YAxis tickFormatter={(value) => `${formatNumber(value)} kWh`} width={yAxisWidth(120)} />
            <ReferenceLine y={0} stroke="#000" />
            <Tooltip formatter={(value: number) => `${formatNumber(value, 2)} kWh`} />
            <Legend />
            {objectValues(orgMap).map((o) =>
              <Bar key={o._id} dataKey={`${o._id}_production`} barSize={40} stackId="a" name={`${orgMap[o._id].name}`} fill={orgMap[o._id].primaryColor} isAnimationActive={false} />
            )}
            <Bar dataKey="forecasted_production" barSize={20} stackId="a" name={strings.Forecasted()} fill={css.grey6} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </RicDashboardQuarterlyProductionRevenueChartStyled>
  );
};
/** Stacked bar chart of modeled solar revenue by portfolio across the sampled project years (Y1–Y25).
 *  Everything here is forecast, so — unlike the YTD/Quarterly chart — there is no actual/forecast split
 *  and no current-period highlight. */
export const RicDashboard25YearRevenueChart = (props: { data: Ric25YearRevenueData }) => {
  const { orgMap, records } = props.data;
  return (
    <RicDashboardSolarRevenueChartStyled className="solar-revenue-chart">
      <h1>{strings.SolarRevenue()}</h1>
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={records}>
            <XAxis dataKey="key" name="key" />
            <YAxis tickFormatter={(value) => `${formatCurrency(value, 0)}`} width={yAxisWidth(100)} />
            <ReferenceLine y={0} stroke="#000" />
            <Tooltip formatter={(value: number) => `$${formatNumber(value, 2)}`} />
            <Legend />
            {objectValues(orgMap).map((o) =>
              <Bar key={o._id} dataKey={`${o._id}_revenue`} barSize={40} stackId="a" name={`${orgMap[o._id].name}`} fill={orgMap[o._id].primaryColor} isAnimationActive={false} />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </RicDashboardSolarRevenueChartStyled>
  );
};

/** RIC dashboard, 25-year modeled-revenue view — the third sibling of the YTD / Quarterly pages. */
export const RicDashboardPage25Year = () => {
  const user = useUser();
  const userId = user?._id;
  const orgId = user?.orgId;
  const account = useFindOne(() => Accounts.find({ orgId, userId }), [`${orgId}_${userId}`]);
  const [systems, loadingSystems] = useMethod(ricDashboardSystems, undefined);
  const [data, loadingData] = useMethod(ricSolar25YearRevenueChartData, undefined);
  const loading = loadingSystems || loadingData;
  if (user && !user.isSuperUser && account && !account.roles?.includes(Roles.ricExecutive)) return <NotPermittedPage />;
  if (loading || !data) return <LoadingFlex />;
  return (
    <RicDashboardPageStyled>
      <PageHeading
        left={<h1>Realty Income Dashboard</h1>}
        right={
          <div>
            <Link to="/ric-dashboard/ytd"><Button className="btn secondary">Year to Date</Button></Link>
            <Link to="/ric-dashboard/quarterly"><Button className="btn secondary">Quarterly</Button></Link>
            <Button>25-Year</Button>
          </div>
        }
      />
      <PageBody>
        <RicDashboardAggregates
          items={[
            { label: 'Total Number of Systems', value: `${systems?.numSystems || 0}` },
            { label: 'Total System Size', value: `${formatNumber(systems?.systemSize || 0)} kW DC` },
            { label: 'Year 1 Revenue', value: `${formatCurrency(data.totals.y1, 0)}` },
            { label: 'Year 25 Revenue', value: `${formatCurrency(data.totals.y25, 0)}` },
            { label: '25-Year Total Revenue', value: `${formatCurrency(data.totals.total, 0)}` },
            { label: 'Average Annual Revenue', value: `${formatCurrency(data.totals.avgAnnual, 0)}` },
          ]}
        />
        <RicDashboard25YearRevenueChart data={data} />
      </PageBody>
    </RicDashboardPageStyled>
  );
};
